import { clsx } from '@alexis/helpers/clsx';
import { appendSearchParams } from '@alexis/helpers/searchParam';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { translateNumber, translateText } from '@wego/alexis/helpers/translation';

import HotelAmenityIcon from '@components/hotels/HotelAmenityIcon';
import Image from '@components/Image';
import Price from '@components/Price';

import {
  SHOW_POPULAR_FLAG,
  DISPLAY_3_LATEST_REVIEWS,
  HOTEL_IMAGES_CAROUSEL_CURSOR_NAVIGATION,
} from '@constants/hotels';

import { GlobalContext } from '@context/GlobalContext';

import {
  getHotelSortType,
  getDailyVersion,
  mod,
  countryCodeToFlagEmoji,
  getPartnerName,
} from '@helpers/hotel';
import {
  cloudflareImageTransformations,
  cloudinaryImageTransformations,
  isCloudinarySrc,
} from '@helpers/imageTransformations';
import { convertHotelRatePriceToPrice, getBestDealUsualPrice } from '@helpers/price';
import { getReviewScoreDisplay } from '@helpers/review';
import { hotelsRoute } from '@helpers/routeTranslation';

import CircleIcon from '@icons/circle_small.svg';
import CloseIcon from '@icons/close_small.svg';
import CalendarIcon from '@icons/new/calendar.svg';
import PopularIcon from '@icons/popular.svg';
import PromoIcon from '@icons/promo.svg';
import StarIcon from '@icons/star_rounded.svg';
import TickIcon from '@icons/tick_small.svg';

import { useInterval } from '@hooks/useInterval';
import { useReviewRatingType } from '@hooks/useReviewRatingType';

import { triggerService } from '@observables/changeDatesObservables';

import {
  getCurrencyState,
  getCurrentSiteState,
  getExchangeRateState,
  getIsRtlState,
  getLocaleState,
  getPriceOptionsState,
  getTranslationsState,
} from '@redux/selectors';

import { Currency } from '@wegoTypes/currency';
import type { CurrentSite } from '@wegoTypes/currentSite';
import { HotelSortType } from '@wegoTypes/hotels/hotelSortType';
import { HotelViewModel } from '@wegoTypes/hotels/hotelViewModel';
import { HotelLatestReviews } from '@wegoTypes/hotels/metasearch/hotelLatestReviews';
import { HotelSearchResultHotel } from '@wegoTypes/hotels/metasearch/hotelSearchResultHotel';
import { RateViewModel } from '@wegoTypes/hotels/rateViewModel';
import { Translations } from '@wegoTypes/translations';

import styles from './HotelCard.module.scss';

export interface HotelCardProps {
  additionalUrlTrackingParams?: { [key: string]: string | number };
  hasTabbyGateway: boolean;
  hotelViewModel: HotelViewModel;
  isHotelFromGoogle?: boolean;
  localeParam: string | undefined;
  onHotelCardClick: (
    selectedRate: RateViewModel,
    hotelViewModel: HotelViewModel,
    typeOfClick: 'sponsoredAd' | 'rate' | 'detail',
  ) => void;
  searchId: string;
  showSponsoredAd: boolean;
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
  isShowReview: boolean;
  setListRenderId?: React.Dispatch<React.SetStateAction<{ [key: number]: boolean }>>;
  hotelLatestReviews?: Array<HotelLatestReviews>;
  isSelectedHotel?: boolean;
  isLoadingRates?: boolean;
}

const HotelCard: React.FC<HotelCardProps> = ({
  additionalUrlTrackingParams,
  hasTabbyGateway,
  hotelViewModel,
  isHotelFromGoogle,
  localeParam,
  onHotelCardClick,
  searchId,
  showSponsoredAd,
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
  isShowReview,
  setListRenderId,
  hotelLatestReviews,
  isSelectedHotel,
  isLoadingRates,
}): JSX.Element => {
  const currency: Currency = useSelector(getCurrencyState);
  const currentSite: CurrentSite = useSelector(getCurrentSiteState);
  const exchangeRate: ExchangeRate = useSelector(getExchangeRateState);
  const isRtl: boolean = useSelector(getIsRtlState);
  const locale: string = useSelector(getLocaleState);
  const priceOptions: PriceOptions = useSelector(getPriceOptionsState);
  const translations: Translations = useSelector(getTranslationsState);

  const [searchParams] = useSearchParams();

  const sortTypeSearchParam = searchParams.get('sort');
  const tsCodeSearchParam = searchParams.get('ts_code');

  const containerDivRef = useRef<HTMLDivElement>(null);
  const containerDivReviewRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { userCentricCoreConfigs } = useContext(GlobalContext);
  const { showTotalPrice, isIncludingTaxOrFee: isIncludingTax } = priceOptions;

  const showPopularFlag =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === SHOW_POPULAR_FLAG)?.value ===
    '1';

  const isDisplayLatestReviews =
    userCentricCoreConfigs.find((coreConfig) => coreConfig.key === DISPLAY_3_LATEST_REVIEWS)
      ?.value === '1';

  const isCursorNavigationEnabled =
    userCentricCoreConfigs.find(
      (coreConfig) => coreConfig.key === HOTEL_IMAGES_CAROUSEL_CURSOR_NAVIGATION,
    )?.value === '1';

  const noImagePlaceholderSrc = cloudinaryImageTransformations(
    'https://assets.wego.com/image/upload/v1639463186/web/misc/hotel-image-placeholder.png',
    'h_240',
    'c_fill',
    'f_auto',
    'fl_lossy',
    'q_auto:low',
    'g_auto',
  );
  const isIpad = /iPad/i.test(navigator.userAgent);

  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedReviewIndex, setSelectedReviewIndex] = useState(0);
  const [fade, setFade] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);

  const { pathname, search } = useLocation();

  const params = useParams<{ districtId?: string; checkInDate: string; checkOutDate: string }>();

  const { getReviewRatingTypeDisplay, getReviewRatingTypeColorClassName } = useReviewRatingType();

  // Remove all filter params from search
  const searchWithoutExtraParams = useMemo<string>(() => {
    const searchParams = new URLSearchParams(search);
    [
      'modal',
      'sort',
      'order',
      'name',
      'deals',
      'amenities',
      'brands',
      'chains',
      'districts',
      'price',
      'accommodation_types',
      'guest_rating',
      'guest_score',
      'stars',
      'open_calendar',
      'selected_hotel_ids',
    ].forEach((param) => {
      searchParams.delete(param);
    });

    const updatedSearchParams = searchParams.toString();

    return !!updatedSearchParams ? `?${updatedSearchParams}` : '';
  }, [search]);

  const directBookingSearchWithoutExtraParams = useMemo<string>(() => {
    const searchParams = new URLSearchParams(searchWithoutExtraParams);

    ['guests', 'providers', 'free_amenities', 'provider_codes'].forEach((param) => {
      searchParams.delete(param);
    });

    const updatedSearchParams = searchParams.toString();

    return !!updatedSearchParams ? `?${updatedSearchParams}` : '';
  }, [searchWithoutExtraParams]);

  const imageUrls = useMemo<Array<string>>(() => {
    if (hotelViewModel.hotel.images.length > 0) {
      return hotelViewModel.hotel.images.map<string>((image) => image.url);
    }
    return [noImagePlaceholderSrc];
  }, [hotelViewModel.hotel.images]);

  // Get best deal rate
  const bestDeal = useMemo<RateViewModel | undefined>(() => {
    if (!!hotelViewModel.filteredRates.length) {
      return hotelViewModel.filteredRates[0];
    }
    return undefined;
  }, [hotelViewModel.filteredRates]);

  // Get best deal usual rate
  const bestDealUsualPrice = getBestDealUsualPrice(bestDeal, priceOptions);

  const isPayInInstallmentAllowed = useMemo<boolean>(() => {
    if (!!bestDeal && bestDeal.provider.directBooking && hasTabbyGateway) {
      const hasPayLaterOrPayInHotel = bestDeal.paymentTypeRateAmenities.some(
        (paymentTypeRateAmenity) =>
          paymentTypeRateAmenity.code === 'PAY_AT_THE_HOTEL' ||
          paymentTypeRateAmenity.code === 'PAY_LATER',
      );

      return !hasPayLaterOrPayInHotel;
    }
    return false;
  }, [bestDeal, hasTabbyGateway]);

  const sortedAmenities = useMemo(() => {
    return hotelViewModel.hotel.amenityIds?.length
      ? hotelViewModel.hotel.amenityIds.map((id) =>
          hotelViewModel.amenities.find((amenity) => amenity.id === id),
        )
      : hotelViewModel.amenities;
  }, [hotelViewModel.amenities, hotelViewModel.hotel.amenityIds]);

  const formatedDateRange = useMemo((): string => {
    if (!params.checkInDate || !params.checkOutDate) {
      return '';
    }
    const start = new Date(params.checkInDate);
    const end = new Date(params.checkOutDate);

    const startDay = start.getDate();
    const endDay = end.getDate();
    const startMonth = start.toLocaleDateString(locale, { month: 'short' });
    const endMonth = end.toLocaleDateString(locale, { month: 'short' });
    const year = start.getFullYear();

    let formattedDate =
      start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
        ? `${startDay}-${endDay} ${startMonth} ${year}`
        : `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;

    // Handle Right-to-Left (RTL) format
    if (isRtl) {
      formattedDate =
        start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
          ? `${year} ${startMonth} ${endDay}-${startDay}`
          : `${year} ${endMonth} ${endDay} - ${startMonth} ${startDay}`;
    }

    return `(${formattedDate})`;
  }, [locale, isRtl, params.checkInDate, params.checkOutDate]);

  const handleLeftChevronClick = useCallback<() => void>(() => {
    const newSelectedImageIndex = selectedImageIndex - 1;

    setSelectedImageIndex(newSelectedImageIndex < 0 ? imageUrls.length - 1 : newSelectedImageIndex);
  }, [selectedImageIndex, imageUrls]);

  const handleRightChevronClick = useCallback<() => void>(() => {
    const newSelectedImageIndex = selectedImageIndex + 1;

    setSelectedImageIndex(newSelectedImageIndex > imageUrls.length - 1 ? 0 : newSelectedImageIndex);
  }, [selectedImageIndex, imageUrls]);

  const handleMouseEnter = useCallback(() => {
    if (!hotelLatestReviews || hotelLatestReviews?.length < 2) return;
    isInitialRender && setIsInitialRender(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      setFade(false); // Start fade-out

      setTimeout(() => {
        setSelectedReviewIndex((prevIndex) => {
          const newSelectedReviewIndex = prevIndex + 1;
          const updatedIndex =
            newSelectedReviewIndex >= hotelLatestReviews.length ? 0 : newSelectedReviewIndex;

          return updatedIndex;
        });

        setFade(true); // Start fade-in after content update
      }, 500); // Duration of the fade-out transition
    }, 1500);
  }, [hotelLatestReviews?.length]);

  const handleMouseLeave = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setFade(true);
  }, []);

  const hotelDetailHref = useCallback<(hotel: HotelSearchResultHotel) => string>(
    (hotel: HotelSearchResultHotel) => {
      const pathname$ = !!params.districtId
        ? pathname.replace(`/d${params.districtId}`, '')
        : pathname;

      return appendSearchParams(
        `${window.location.origin}${pathname$}/${hotel.id}${searchWithoutExtraParams}`,
        { search_id: searchId },
      );
    },
    [pathname, params.districtId, searchWithoutExtraParams, searchId],
  );

  const rateHandoffHref = useCallback<(isSponsoredAd: boolean, rate: RateViewModel) => string>(
    (isSponsoredAd: boolean, rate: RateViewModel) => {
      if (!isSponsoredAd && rate.provider!.directBooking) {
        return appendSearchParams(
          `${hotelsRoute(
            currentSite,
            localeParam,
          )}/booking/checkout${directBookingSearchWithoutExtraParams}`,
          { search_id: searchId, reference_id: rate.id },
        );
      }
      return appendSearchParams(rate.handoffUrl, {
        ...additionalUrlTrackingParams,
        wc: wegoAnalyticsClientId,
        ws: wegoAnalyticsClientSessionId,
      });
    },
    [
      localeParam,
      directBookingSearchWithoutExtraParams,
      searchId,
      additionalUrlTrackingParams,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
    ],
  );

  const handleOnSponsorAdContainerClick = useCallback<() => void>(() => {
    window.open(rateHandoffHref(true, bestDeal!), '_blank');
    onHotelCardClick(bestDeal!, hotelViewModel, 'sponsoredAd');
  }, [rateHandoffHref, onHotelCardClick, bestDeal, hotelViewModel]);

  const handleOnHotelDetailClick = useCallback<(e: React.MouseEvent<HTMLAnchorElement>) => void>(
    (e) => {
      e.stopPropagation();

      if (!!hotelViewModel.sponsoredAd) {
        onHotelCardClick(bestDeal!, hotelViewModel, 'sponsoredAd');
      } else {
        onHotelCardClick(bestDeal!, hotelViewModel, 'detail');
      }
    },
    [hotelViewModel, onHotelCardClick, bestDeal],
  );

  const handleOnDealClick = useCallback<
    (e: React.MouseEvent<HTMLAnchorElement>, rate: RateViewModel) => void
  >(
    (e, rate) => {
      e.stopPropagation();
      onHotelCardClick(rate, hotelViewModel, 'rate');
    },
    [hotelViewModel, onHotelCardClick],
  );

  const getImageTransformationsUrl = useCallback((url: string, height = 240) => {
    const imgUrl = url ?? noImagePlaceholderSrc;
    if (isCloudinarySrc(imgUrl)) {
      return cloudinaryImageTransformations(
        imgUrl,
        `h_${height}`,
        'c_fill',
        'f_auto',
        'fl_lossy',
        'q_auto:best',
        'g_auto',
      );
    } else {
      return cloudflareImageTransformations(imgUrl, { format: 'auto', height, quality: 90 });
    }
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        setIsInitialRender(true);
      }
    };
  }, []);

  const getImagePosition = (imageContainerIndex: number) => {
    const imageContainerWidth = containerDivRef.current?.clientWidth ?? 0;
    const position = mod(imageContainerIndex - selectedImageIndex, imageUrls.length);

    return position * imageContainerWidth;
  };

  const getReviewPosition = (reviewContainerIndex: number) => {
    const reviewContainerWidth = containerDivReviewRef.current?.clientWidth ?? 0;
    const position = mod(
      reviewContainerIndex - selectedReviewIndex,
      hotelLatestReviews?.length || 0,
    );

    return position > 0 ? position * reviewContainerWidth + 2 : position * reviewContainerWidth;
  };

  const getReviewTime = useCallback(
    (timestamp: string) => {
      const now = new Date();
      const reviewDate = new Date(timestamp);
      const diffInMilliseconds = now.getTime() - reviewDate.getTime();
      const diffInSeconds = Math.floor(diffInMilliseconds / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInSeconds < 3600) {
        return translations.just_now;
      } else if (diffInHours < 24) {
        return `${diffInHours} ${
          diffInHours === 1 ? translations.hour_ago : translations.hours_ago
        }`;
      } else if (diffInDays === 1) {
        return translations.yesterday;
      } else if (diffInDays <= 6) {
        return translateText(translations.days_ago, locale, diffInDays);
      } else if (diffInDays === 7) {
        return translations.a_week_ago;
      } else {
        const day = reviewDate.getDate();
        const month = reviewDate.toLocaleString(locale, { month: 'short' });
        const year = reviewDate.getFullYear();
        return `${day} ${month} ${year}`;
      }
    },
    [locale],
  );

  const displayRating = (score: number) => {
    if (!!score && score < 0) return '';
    const roundedScore: number = Math.round(score * 10) / 10;
    const rating: string = getReviewRatingTypeDisplay(score * 10, translations);

    const formattedRating: string = `${roundedScore} ${rating}`;

    return formattedRating;
  };

  useInterval(() => {
    setListRenderId?.((prev) => ({ ...prev, [hotelViewModel.hotelId]: true }));
  }, 2000);

  useEffect(() => {
    if (isShowReview) {
      setFade(true);
    }
  }, [isShowReview]);

  const renderReviews = useCallback(() => {
    if (!hotelViewModel?.hotel?.aiReviews.length) return null;
    return (
      <div className={clsx(styles.reviews, fade && styles.show)}>
        <div className={styles.reviewTagLineContainer}>
          {hotelViewModel?.hotel?.aiReviews.map((review) => (
            <div key={review.id} className={styles.reviewTagLineContent}>
              {review.sentiment === 'positive' ? (
                <TickIcon className={clsx(styles.icon, styles.tickIcon)} />
              ) : (
                <CloseIcon className={clsx(styles.icon, styles.closeIcon)} />
              )}
              {review.text}
            </div>
          ))}
        </div>
        <div className={styles.line} />
        <div className={styles.reviewTagLineCounter}>
          <CircleIcon />
          {translateText(
            translations.count_review,
            locale,
            hotelViewModel?.hotel?.reviews[0]?.count?.toLocaleString() || '',
          )}
          <span className={styles.counterText}>{translations.summarised_by_ai}</span>
        </div>
      </div>
    );
  }, [hotelViewModel?.hotel?.aiReviews, isShowReview, fade]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!containerDivRef.current || !isCursorNavigationEnabled) return;

      const { left, width: containerWidth } = containerDivRef.current.getBoundingClientRect();
      const xPos = e.clientX - left;
      const segmentWidth = containerWidth / imageUrls.length;

      const newIndex = Math.min(Math.floor(xPos / segmentWidth), imageUrls.length - 1);
      setSelectedImageIndex(newIndex);
    },
    [containerDivRef, imageUrls, isCursorNavigationEnabled],
  );

  const handleOnChangeDates = () => {
    triggerService.triggerFunctionTarget();
  };

  return (
    <div
      className={clsx(
        styles.container,
        !!hotelViewModel.sponsoredAd && styles.isSponsoredAd,
        isHotelFromGoogle && styles.googleHotel,
      )}
      style={
        showSponsoredAd ? { border: `2px solid ${hotelViewModel.sponsoredAd!.brandColor}` } : {}
      }
      onClick={!!hotelViewModel.sponsoredAd ? handleOnSponsorAdContainerClick : undefined}
    >
      {showSponsoredAd && !!hotelViewModel.sponsoredAd ? (
        <div
          className={styles.sponsoredAd}
          style={{ backgroundColor: hotelViewModel.sponsoredAd.brandColor }}
        >
          <img
            src={cloudinaryImageTransformations(
              hotelViewModel.sponsoredAd.brandLogoUrl,
              'w_200',
              'h_82',
              'c_fit',
              'f_auto',
              'fl_lossy',
              'q_auto:low',
            )}
          />

          <div className={styles.brandLabelsContainer}>
            <div className={styles.brandHeader}>{hotelViewModel.sponsoredAd.brandHeader}</div>
            <div className={styles.brandDescription}>
              {hotelViewModel.sponsoredAd.brandDescription}
            </div>
          </div>

          <div className={styles.tag}>{translations.flights_inline_ads_sponsored_tag}</div>
        </div>
      ) : null}

      {isHotelFromGoogle && (
        <div className={styles.googleHotelLabel}>
          {translateText(
            translations.distribution_landing_label,
            locale,
            getPartnerName(tsCodeSearchParam) || 'Google',
          )}
        </div>
      )}

      <div className={styles.hotelContainer}>
        <div
          className={clsx(styles.imageGallery, isIpad && styles.isIpad)}
          onClick={(e) => e.stopPropagation()}
          ref={containerDivRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setSelectedImageIndex(0)}
        >
          {!isCursorNavigationEnabled ? (
            <>
              <Image
                className={clsx(styles.mainImage, !bestDeal && !isLoadingRates && styles.soldOut)}
                isLazy={false}
                noImagePlaceholderSrc={noImagePlaceholderSrc}
                placeholderSrc={noImagePlaceholderSrc}
                src={getImageTransformationsUrl(imageUrls[selectedImageIndex])}
              />

              <div className={styles.thumbnailImages}>
                {[...Array(4).keys()].map((index: number) => {
                  return (
                    <div
                      key={index}
                      className={styles.imageWithText}
                      onMouseEnter={() => setSelectedImageIndex(index + 1)}
                    >
                      <Image
                        className={styles.thumbnailImage}
                        isLazy={false}
                        noImagePlaceholderSrc={noImagePlaceholderSrc}
                        placeholderSrc={noImagePlaceholderSrc}
                        src={getImageTransformationsUrl(imageUrls[index + 1], 138)}
                      />
                      {index === 3 && hotelViewModel.hotel.imagesCount > 4 ? (
                        <div className={styles.remaining}>
                          {hotelViewModel.hotel.imagesCount - 4}+
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              {[...Array(imageUrls.length).keys()].map((index: number) => {
                return (
                  <Image
                    key={index}
                    className={styles.image}
                    isLazy={false}
                    noImagePlaceholderSrc={noImagePlaceholderSrc}
                    placeholderSrc={noImagePlaceholderSrc}
                    src={getImageTransformationsUrl(imageUrls[index])}
                    style={{
                      [isRtl ? 'right' : 'left']: `${getImagePosition(index)}px`,
                    }}
                  />
                );
              })}

              {imageUrls.length > 1 ? (
                <>
                  <div className={styles.indicators}>
                    {[...Array(imageUrls.length).keys()].map((index: number) => (
                      <div
                        key={index}
                        className={clsx(
                          styles.indicator,
                          index === selectedImageIndex && styles.selected,
                          (index === selectedImageIndex - 1 || index === selectedImageIndex + 1) &&
                            styles.medium,
                        )}
                      ></div>
                    ))}
                  </div>

                  {!isCursorNavigationEnabled ? (
                    <>
                      <div
                        className={clsx(styles.chevronContainer, styles.left)}
                        onClick={handleLeftChevronClick}
                      >
                        <i className={clsx(styles.chevron, isRtl ? styles.right : styles.left)}></i>
                      </div>
                      <div
                        className={clsx(styles.chevronContainer, styles.right)}
                        onClick={handleRightChevronClick}
                      >
                        <i className={clsx(styles.chevron, isRtl ? styles.left : styles.right)}></i>
                      </div>
                    </>
                  ) : null}
                </>
              ) : null}
            </>
          )}
        </div>

        {/* Sponsored Tag */}
        {!showSponsoredAd && !!hotelViewModel.sponsoredAd ? (
          <div className={clsx(styles.tag, styles.sponsored)}>
            {translations.flights_inline_ads_sponsored_tag}
          </div>
        ) : null}

        {/* Apartment Tag */}
        {!hotelViewModel.sponsoredAd &&
        !!hotelViewModel.propertyType &&
        hotelViewModel.propertyType.id === 4 ? (
          <div className={clsx(styles.tag, styles.propertyType)}>
            {hotelViewModel.propertyType.name}
          </div>
        ) : null}

        {/* Sold Out Tag */}
        {!bestDeal && !isLoadingRates && isSelectedHotel ? (
          <div className={clsx(styles.tag, styles.soldOutType)}>{translations.sold_out}</div>
        ) : null}

        <div className={styles.detailContainer}>
          <a
            className={styles.detail}
            href={
              !!hotelViewModel.sponsoredAd
                ? rateHandoffHref(true, bestDeal!)
                : hotelDetailHref(hotelViewModel.hotel)
            }
            onClick={handleOnHotelDetailClick}
            target='_blank'
            rel='noreferrer'
          >
            <div className={styles.hotelDetail}>
              {/* Hotel Name */}
              <div className={styles.name}>{hotelViewModel.hotel.name}</div>

              <div className={styles.starsAndDistrictAndDistanceFrom}>
                {/* Star */}
                {!!hotelViewModel.hotel.star ? (
                  <div className={styles.stars}>
                    {[...Array(hotelViewModel.hotel.star).keys()].map((value: number) => {
                      return <StarIcon key={value} />;
                    })}
                  </div>
                ) : null}

                {/* District */}
                {!!hotelViewModel.district ? (
                  <div className={styles.district}>{hotelViewModel.district.name}</div>
                ) : null}

                {/* Distance From */}
                {!!sortTypeSearchParam &&
                ((getHotelSortType(sortTypeSearchParam) === HotelSortType.NearestToAirport &&
                  hotelViewModel.hotel.distanceToNearestAirport) ||
                  (getHotelSortType(sortTypeSearchParam) === HotelSortType.NearestToCityCentre &&
                    hotelViewModel.hotel.distanceToCityCentre) ||
                  (getHotelSortType(sortTypeSearchParam) ===
                    HotelSortType.NearestToTargetLocation &&
                    hotelViewModel.hotel.distanceToSearchLocation)) ? (
                  <>
                    <div className={styles.dotSeparator}>・</div>

                    <div className={styles.distanceFrom}>
                      {getHotelSortType(sortTypeSearchParam) === HotelSortType.NearestToAirport
                        ? translateText(
                            translations.to_airport,
                            locale,
                            `${hotelViewModel.hotel.distanceToNearestAirport!.toFixed(2)} km`,
                          )
                        : getHotelSortType(sortTypeSearchParam) ===
                          HotelSortType.NearestToTargetLocation
                        ? translateText(
                            translations.distance_target_location,
                            locale,
                            `${hotelViewModel.hotel.distanceToSearchLocation!.toFixed(2)} km`,
                          )
                        : translateText(
                            translations.distance_city_centre,
                            locale,
                            `${hotelViewModel.hotel.distanceToCityCentre!.toFixed(2)} km`,
                          )}
                    </div>
                  </>
                ) : null}
              </div>

              {/* Review */}
              {!!hotelViewModel.hotel.reviews[0] ? (
                <div className={styles.reviewContainer}>
                  <div className={styles.review}>
                    <div
                      className={clsx(
                        styles.score,
                        getReviewRatingTypeColorClassName(
                          hotelViewModel.hotel.reviews[0].score,
                          styles,
                        ),
                      )}
                    >
                      {translateNumber(
                        getReviewScoreDisplay(hotelViewModel.hotel.reviews[0].score),
                        locale === 'fa',
                      )}
                    </div>

                    <div className={styles.ratingType}>
                      {getReviewRatingTypeDisplay(
                        hotelViewModel.hotel.reviews[0].score,
                        translations,
                      )}
                    </div>

                    <div className={styles.reviewCount}>
                      (
                      {translateText(
                        translations.count_review,
                        locale,
                        hotelViewModel.hotel.reviews[0]?.count,
                      )}
                      )
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Popular badge */}
              {!!hotelViewModel.socialProofs && showPopularFlag ? (
                <div className={styles.popularBadge}>
                  <PopularIcon /> {translations.in_popular_demand}
                </div>
              ) : null}

              {/* Amenities */}
              <div className={styles.amenities}>
                {sortedAmenities.slice(0, 2).map((amenity) => {
                  if (!amenity) {
                    return null;
                  }

                  return (
                    <div key={amenity.id} className={styles.amenity}>
                      <HotelAmenityIcon
                        amenityId={amenity.id}
                        className={styles.hotelAmenitiesIcon}
                      />
                      {amenity.name}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Customer Reviews */}
            {isDisplayLatestReviews && hotelLatestReviews?.length ? (
              <div
                className={clsx(styles.reviewsContainer, isIpad && styles.isIpad)}
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={() => handleMouseEnter()}
                onMouseLeave={handleMouseLeave}
                ref={containerDivReviewRef}
              >
                {[...Array(hotelLatestReviews.length).keys()].map((index: number) => {
                  return (
                    <div
                      key={index}
                      className={clsx(
                        styles.reviewWrapper,
                        !isInitialRender && styles.fadeEnter,
                        fade && styles.fadeEnterActive,
                      )}
                      style={{
                        [isRtl ? 'right' : 'left']: `${getReviewPosition(index)}px`,
                      }}
                    >
                      <div key={index} className={styles.reviewHeader}>
                        <div className={styles.userInfo}>
                          <div className={styles.reviewName}>
                            {hotelLatestReviews[index]?.author?.name || translations.anonymous}
                          </div>
                          <div className={styles.reviewCountry}>
                            {countryCodeToFlagEmoji(
                              hotelLatestReviews[index]?.author?.country_code,
                            )}
                          </div>
                        </div>
                        <span className={styles.reviewName}>·</span>
                        <div className={styles.reviewTime}>
                          {getReviewTime(hotelLatestReviews[index].posted_at)}
                        </div>
                        <span className={styles.reviewName}>·</span>
                        <div className={styles.reviewExceptional}>
                          {displayRating(hotelLatestReviews[index]?.rating || 0)}
                        </div>
                      </div>
                      <div className={styles.reviewComment}>
                        {hotelLatestReviews[index].comment}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {/* Review Taglines */}
            {!isDisplayLatestReviews ? (
              <div className={styles.reviewTaglinesWrapper}>
                <div className={clsx(styles.loaderWrapper, isShowReview && styles.hide)}>
                  <div className={styles.loader} /> {translations.summarising_reviews}
                </div>
                {isShowReview ? renderReviews() : null}
              </div>
            ) : null}
          </a>

          {/* Best Deal */}
          {!!bestDeal ? (
            <a
              className={styles.bestDeal}
              href={
                bestDeal.provider!.directBooking
                  ? hotelDetailHref(hotelViewModel.hotel)
                  : rateHandoffHref(!!hotelViewModel.sponsoredAd, bestDeal)
              }
              target='_blank'
              rel='noreferrer'
              onClick={(e) => handleOnDealClick(e, bestDeal)}
            >
              <div className={styles.deal}>
                <div className={styles.providerLogoContainer}>
                  {/* Promo */}
                  {!!bestDeal.promos?.length ? <PromoIcon className={styles.promo} /> : null}

                  {/* Provider */}
                  <img
                    className={styles.providerLogo}
                    src={`https://assets.wego.com/image/upload/c_fit,h_40,f_auto,q_auto/${getDailyVersion()}/providers/rectangular_logos/${
                      bestDeal.provider!.code
                    }.png`}
                  />
                </div>

                {/* Discounted Percentage */}
                {!!bestDeal.usualPrice ? (
                  <div className={styles.discountedPercentage}>{`${Math.round(
                    bestDeal.usualPrice.discountToUsualAmount * 100,
                  )}% Off`}</div>
                ) : null}

                {/* Hotel Rate */}
                <div className={styles.tooltipContainer}>
                  <Price
                    isInline
                    className={styles.price}
                    price={convertHotelRatePriceToPrice(
                      bestDeal.price,
                      showTotalPrice,
                      isIncludingTax,
                    )}
                    locale={locale}
                    currency={currency}
                    exchangeRate={exchangeRate}
                  />

                  {!!bestDeal.promos?.length ? (
                    <div className={clsx(styles.tooltip, styles[isRtl ? 'right' : 'left'])}>
                      {bestDeal.promos[0].shortMessage}
                    </div>
                  ) : null}
                </div>

                {/* Strikethrough Hotel Rate */}
                {!!bestDealUsualPrice ? (
                  <Price
                    isInline
                    isStrikethrough
                    className={styles.usualPrice}
                    price={bestDealUsualPrice}
                    locale={locale}
                    currency={currency}
                    exchangeRate={exchangeRate}
                  />
                ) : null}

                {/* Hotel Rate Type */}
                <div className={styles.priceType}>
                  {showTotalPrice ? translations.total_stay : translations.hotel_per_night}
                </div>

                {/* Payment Type Rate Amenities */}
                {bestDeal.paymentTypeRateAmenities.map((amenity) => (
                  <div key={amenity.id} className={styles.paymentTypeRateAmenity}>
                    {amenity.name}
                  </div>
                ))}

                {/* Pay in Installment */}
                {isPayInInstallmentAllowed ? (
                  <div className={styles.payInInstallment}>
                    <img
                      src={cloudinaryImageTransformations(
                        'https://assets.wego.com/image/upload/v1663418328/web/payment_cards/tabby_square_3x.png',
                        'c_fill',
                        'fl_lossy',
                        'q_auto:low',
                        'f_auto',
                        'w_48',
                        'h_48',
                      )}
                    />
                    <div>{translations.lbl_interest_free_payments}</div>
                  </div>
                ) : null}
              </div>

              {/* Book Now / View Deal */}
              <div
                className={clsx(
                  styles.viewDeal,
                  !showSponsoredAd &&
                    bestDeal.provider!.directBooking &&
                    styles.darkGreenBackground,
                  showSponsoredAd && hotelViewModel.sponsoredAd && styles.sponsoredAd,
                )}
                style={
                  showSponsoredAd && hotelViewModel.sponsoredAd
                    ? { backgroundColor: `${hotelViewModel.sponsoredAd.brandColor}` }
                    : {}
                }
              >
                {bestDeal.provider!.directBooking ? translations.book_now : translations.view_deal}
              </div>
            </a>
          ) : null}

          {!bestDeal && isSelectedHotel && (
            <div className={styles.noRateContainer}>
              {isLoadingRates ? (
                <div className={styles.loaderWrapper}>
                  <div className={styles.loader} /> {translations.searching_for_rates}
                </div>
              ) : (
                <>
                  <div className={styles.noRateWarning}>
                    <span>
                      {translateText(translations.sold_out_message, locale, formatedDateRange)}
                    </span>
                  </div>
                  <button className={styles.changeDatesCTA} onClick={() => handleOnChangeDates()}>
                    <CalendarIcon /> {translations.change_dates}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HotelCard;
